/**
 * @module utils/entity-type-mapper
 * @description Maps Insightly entity type numeric codes to frontend entity configurations
 *
 * This module bridges the backend C# enum (InsightlyEntityType) with the frontend
 * entity type system. The backend returns numeric entity type codes (0-3) which need
 * to be mapped to the corresponding entity type keys used throughout the frontend.
 */

import { ENTITY_TYPES } from '../features/entity/entity-types.js';
import { logger } from './logger.js';

/**
 * Insightly Entity Type Enum
 * Maps to the C# backend enum InsightlyEntityType
 *
 * @constant {Object} InsightlyEntityType
 * @property {number} PROJECT - Project entity type (0)
 * @property {number} OPPORTUNITY - Opportunity entity type (1)
 * @property {number} LEAD - Lead entity type (2)
 * @property {number} CONTACT - Contact entity type (3)
 */
export const InsightlyEntityType = {
    PROJECT: 0,
    OPPORTUNITY: 1,
    LEAD: 2,
    CONTACT: 3
};

/**
 * Entity type number to entity key mapping
 * Maps numeric codes from the backend to entity type keys used in ENTITY_TYPES
 *
 * @constant {Object} ENTITY_TYPE_MAP
 * @private
 */
const ENTITY_TYPE_MAP = {
    0: 'projects',
    1: 'opportunities',
    2: 'leads',
    3: 'contacts'
};

/**
 * Maps an entity type number (from backend enum) to the corresponding entity type key
 *
 * @param {number} entityTypeNumber - Numeric entity type code (0-3)
 * @returns {string|null} Entity type key ('projects', 'opportunities', 'leads', 'contacts') or null if invalid
 *
 * @example
 * const entityKey = getEntityTypeKey(3); // Returns 'contacts'
 * const config = ENTITY_TYPES[entityKey]; // Get full entity configuration
 */
export function getEntityTypeKey(entityTypeNumber) {
    // Ensure we're working with a number
    const typeNum = typeof entityTypeNumber === 'string' ? parseInt(entityTypeNumber, 10) : entityTypeNumber;

    // Validate the type number
    if (typeof typeNum !== 'number' || isNaN(typeNum)) {
        logger.warn('Invalid entity type number:', entityTypeNumber);
        return null;
    }

    const entityKey = ENTITY_TYPE_MAP[typeNum];

    if (!entityKey) {
        logger.warn('Unknown entity type number:', typeNum);
        return null;
    }

    return entityKey;
}

/**
 * Maps an entity type number to the full entity configuration
 *
 * @param {number} entityTypeNumber - Numeric entity type code (0-3)
 * @returns {Object|null} Entity configuration object from ENTITY_TYPES or null if invalid
 *
 * @example
 * const config = mapEntityType(0);
 * console.log(config.label); // 'Project'
 * console.log(config.icon);  // '📁'
 */
export function mapEntityType(entityTypeNumber) {
    const entityKey = getEntityTypeKey(entityTypeNumber);

    if (!entityKey) {
        return null;
    }

    const config = ENTITY_TYPES[entityKey];

    if (!config) {
        logger.error('Entity configuration not found for key:', entityKey);
        return null;
    }

    return {
        ...config,
        entityType: entityKey // Include the entity type key in the config
    };
}

/**
 * Gets URL query parameters for navigating to a specific entity
 *
 * @param {number} entityTypeNumber - Numeric entity type code (0-3)
 * @param {string} entityId - Entity ID
 * @returns {Object|null} Object with paramKey and entityId, or null if invalid
 *
 * @example
 * const params = getEntityNavigationParams(3, '12345');
 * // Returns: { paramKey: 'contactId', entityId: '12345' }
 * // Use to build URL: `?contactId=12345`
 */
export function getEntityNavigationParams(entityTypeNumber, entityId) {
    const config = mapEntityType(entityTypeNumber);

    if (!config) {
        return null;
    }

    return {
        paramKey: config.paramKey,
        entityId: entityId
    };
}

/**
 * Builds a navigation URL for a linked entity
 *
 * @param {number} entityTypeNumber - Numeric entity type code (0-3)
 * @param {string} entityId - Entity ID
 * @returns {string} Navigation URL (e.g., '?contactId=12345') or '#' if invalid
 *
 * @example
 * const url = buildEntityNavigationUrl(0, '12345');
 * // Returns: '?projectId=12345'
 */
export function buildEntityNavigationUrl(entityTypeNumber, entityId) {
    const params = getEntityNavigationParams(entityTypeNumber, entityId);

    if (!params) {
        logger.warn('Cannot build navigation URL for invalid entity type:', entityTypeNumber);
        return '#';
    }

    return `?${params.paramKey}=${encodeURIComponent(entityId)}`;
}

/**
 * Formats a linked entity object for display
 * Enriches the raw API data with frontend configuration
 *
 * @param {Object} linkedEntity - Raw linked entity from API
 * @param {string} linkedEntity.entityId - Entity ID
 * @param {string} linkedEntity.entityName - Entity name
 * @param {number} linkedEntity.entityType - Numeric entity type code (0-3)
 * @returns {Object|null} Formatted entity object with config, or null if invalid
 *
 * @example
 * const rawEntity = { entityId: '12345', entityName: 'John Doe', entityType: 3 };
 * const formatted = formatLinkedEntity(rawEntity);
 * // Returns:
 * // {
 * //   entityId: '12345',
 * //   entityName: 'John Doe',
 * //   entityType: 3,
 * //   entityTypeKey: 'contacts',
 * //   config: { label: 'Contact', icon: '👤', ... },
 * //   navigationUrl: '?contactId=12345'
 * // }
 */
export function formatLinkedEntity(linkedEntity) {
    if (!linkedEntity || typeof linkedEntity !== 'object') {
        logger.warn('Invalid linked entity object:', linkedEntity);
        return null;
    }

    const { entityId, entityName, entityType } = linkedEntity;

    // Validate required fields
    if (!entityId || !entityName || entityType === undefined || entityType === null) {
        logger.warn('Linked entity missing required fields:', linkedEntity);
        return null;
    }

    const config = mapEntityType(entityType);
    const entityTypeKey = getEntityTypeKey(entityType);
    const navigationUrl = buildEntityNavigationUrl(entityType, entityId);

    if (!config || !entityTypeKey) {
        logger.warn('Could not map entity type for linked entity:', linkedEntity);
        return null;
    }

    return {
        entityId,
        entityName,
        entityType,
        entityTypeKey,
        config,
        navigationUrl
    };
}

/**
 * Validates an array of linked entities and returns only valid ones
 *
 * @param {Array} linkedEntities - Array of linked entity objects
 * @returns {Array} Array of validated and formatted linked entities
 *
 * @example
 * const entities = validateLinkedEntities([
 *   { entityId: '1', entityName: 'Valid', entityType: 0 },
 *   { entityId: '2', entityName: 'Invalid', entityType: 99 },
 *   null
 * ]);
 * // Returns array with only the valid entity
 */
export function validateLinkedEntities(linkedEntities) {
    if (!Array.isArray(linkedEntities)) {
        logger.warn('linkedEntities is not an array:', linkedEntities);
        return [];
    }

    return linkedEntities
        .map(entity => formatLinkedEntity(entity))
        .filter(entity => entity !== null);
}

/**
 * Gets the display label for an entity type number
 *
 * @param {number} entityTypeNumber - Numeric entity type code (0-3)
 * @returns {string} Display label (e.g., 'Project', 'Contact') or 'Unknown'
 *
 * @example
 * const label = getEntityTypeLabel(2); // Returns 'Lead'
 */
export function getEntityTypeLabel(entityTypeNumber) {
    const config = mapEntityType(entityTypeNumber);
    return config ? config.label : 'Unknown';
}

/**
 * Gets the icon for an entity type number
 *
 * @param {number} entityTypeNumber - Numeric entity type code (0-3)
 * @returns {string} Entity icon emoji (e.g., '📁', '👤') or '❓'
 *
 * @example
 * const icon = getEntityTypeIcon(3); // Returns '👤'
 */
export function getEntityTypeIcon(entityTypeNumber) {
    const config = mapEntityType(entityTypeNumber);
    return config ? config.icon : '❓';
}
